// Eskom PARCC — application shell / layout route.
//
// TWO THEMES, ONE APP. This is the single branch point: Legacy renders the
// chrome it has always rendered (Header + flat Nav + <main>), Lumen renders the
// sidebar shell from the redesign. Everything below the frame — the routed
// screens, the data layer, the permission model, the exports — is shared, so a
// control cannot exist in one theme and be missing from the other.
//
// Both paths mount the same ToastProvider, ImpersonationBar and command palette,
// and both sit inside the AuthGate, so a user is signed in by the time either
// mounts.
import { useCallback, useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { ToastProvider } from '../../components/ui';
import { useUiPrefs } from '../../lib/uiPrefs';
import { Header } from './Header';
import { mainClass } from './routes';
import { Nav } from './Nav';
import { ImpersonationBar } from './ImpersonationBar';
import { LumenShell } from './lumen/LumenShell';
import { CommandPalette } from './lumen/CommandPalette';

/** True when the keystroke landed in a field, where `/` is a literal slash. */
function isTypingTarget(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el || !el.tagName) return false;
  const tag = el.tagName.toLowerCase();
  return tag === 'input' || tag === 'textarea' || tag === 'select' || el.isContentEditable === true;
}

/** Legacy chrome, unchanged — plus the `/` command palette, which is a
 *  capability rather than a look and therefore belongs in both themes. */
function LegacyShell(): React.JSX.Element {
  const [paletteOpen, setPaletteOpen] = useState(false);
  const location = useLocation();

  const onKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === '/' && !isTypingTarget(e.target) && !e.metaKey && !e.ctrlKey && !e.altKey) {
      e.preventDefault();
      setPaletteOpen(true);
    }
  }, []);

  useEffect(() => {
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onKeyDown]);

  return (
    <>
      <Header onOpenPalette={() => setPaletteOpen(true)} />
      <Nav />
      {/* Full-bleed screens (the AI workspace) drop the page padding in BOTH
          shells — the flag lives on the route table so Legacy cannot drift. */}
      <main className={mainClass(location.pathname)}>
        <Outlet />
      </main>
      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
    </>
  );
}

export function Shell(): React.JSX.Element {
  const { isLumen } = useUiPrefs();
  return (
    <ToastProvider>
      <ImpersonationBar />
      {isLumen ? <LumenShell /> : <LegacyShell />}
    </ToastProvider>
  );
}
